import {
  Caption1,
  Checkbox,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  Field,
  Input,
  Menu,
  MenuDivider,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  OverlayDrawer,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import {
  Attach16Regular,
  ClipboardTextLtr16Regular,
  Delete16Regular,
  Document16Regular,
  Dismiss24Regular,
  Link16Regular,
  MoreHorizontal20Regular,
  TaskListSquareLtr16Regular,
} from "@fluentui/react-icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { ConfirmDialog } from "@/components/app-dialog";
import { FuiButton, FuiDropdown, FuiFilterSelect, FuiTextarea } from "@/components/fui-control";
import type { ProjectRecord, RecordImpact, RecordSeverity, RecordSource, RecordStatus, RiskScale } from "@/engine/types";
import { useCan, useOwnWork, usePlanSurface } from "@/features/access/use-plan-access";
import { formatDate } from "@/lib/date-format";
import { useCatalogStore } from "@/store/catalog-store";
import { knownAssignees, STATUS_LABEL } from "@/store/execution-task-ops";
import {
  dueLabel,
  hasSeverity,
  IMPACT_LABEL,
  IMPACT_ORDER,
  RECORD_KIND_LABEL,
  RECORD_SECTIONS,
  RISK_SCALE_LABEL,
  RISK_SCALES,
  recordCode,
  riskScore,
  recordsReferencing,
  recordStatusLabel,
  SOURCE_LABEL,
  SOURCE_ORDER,
  supersededBy,
} from "@/store/record-ops";
import { useProjectStore } from "@/store/project-store";
import { AttachmentPanel } from "@/features/tasks/AttachmentPanel";
import { LinkedDocumentRows, useTombstoneSentence } from "@/features/docs/LinkedDocuments";
import { DetailSection, DetailTabs, type DetailTab } from "@/features/tasks/detail-parts";
import { CommentThread, TaskActivityLog, type CommentThreadHandlers } from "@/features/tasks/TaskActivityFeed";
import { openScopeInSchedule } from "@/features/tasks/links";
import { AssigneePicker, DateInput, ScopePicker } from "@/features/tasks/pickers";
import { ScopeChip, useTaskContext } from "@/features/tasks/task-ui";
import { scopeRef } from "@/features/tasks/task-query";
import { RecordChip, RecordStatusBadge, SeverityBadge, recordKindIcon, recordStatusOptions, RefPicker, severityOptions, type RefOption } from "./record-ui";
import { RecordTaskSection } from "./RecordTaskSection";

const useStyles = makeStyles({
  drawer: {
    width: "560px",
    maxWidth: "100vw",
  },
  backdrop: {
    backdropFilter: "blur(2.5px)",
    WebkitBackdropFilter: "blur(2.5px)",
    backgroundColor: "rgba(0, 0, 0, 0.16)",
  },
  header: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
  },
  headerTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: "32px",
    width: "100%",
  },
  headerMeta: {
    display: "inline-flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    color: tokens.colorNeutralForeground3,
    minWidth: 0,
  },
  code: {
    fontVariantNumeric: "tabular-nums",
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: "2px",
    marginLeft: "auto",
  },
  titleInput: {
    width: "100%",
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightRegular,
    lineHeight: tokens.lineHeightBase500,
    "& input": { width: "100%" },
  },
  titleRead: {
    width: "100%",
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightRegular,
    lineHeight: tokens.lineHeightBase500,
    padding: "6px 0",
    color: tokens.colorNeutralForeground1,
  },
  body: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalL,
    paddingBottom: tokens.spacingVerticalXXL,
  },
  bodyFeed: {
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    overflowY: "hidden",
    height: "100%",
    minHeight: 0,
    gap: 0,
  },
  grid2: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: tokens.spacingHorizontalS,
    alignItems: "start",
  },
  impacts: {
    display: "flex",
    flexWrap: "wrap",
    columnGap: tokens.spacingHorizontalM,
    rowGap: tokens.spacingVerticalXXS,
  },
  impactCheck: {
    "& .fui-Checkbox__label": {
      fontSize: tokens.fontSizeBase200,
      lineHeight: tokens.lineHeightBase200,
      paddingTop: tokens.spacingVerticalXS,
      paddingBottom: tokens.spacingVerticalXS,
      paddingLeft: tokens.spacingHorizontalXS,
    },
    "& .fui-Checkbox__indicator": {
      marginTop: tokens.spacingVerticalXS,
      marginBottom: tokens.spacingVerticalXS,
      marginLeft: 0,
    },
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalXS,
  },
  notice: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalXS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
  },
  muted: {
    color: tokens.colorNeutralForeground3,
  },
  footer: {
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: tokens.spacingHorizontalS,
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderTop("1px", "solid", tokens.colorNeutralStroke2),
  },
});

/** Detail editor for the record in `recordFocusId`. Mounted on the project layout so any tab can open it. */
export function RecordDrawer() {
  const styles = useStyles();
  const id = useProjectStore((s) => s.recordFocusId);
  const focusTab = useProjectStore((s) => s.recordFocusTab);
  const focusCommentId = useProjectStore((s) => s.recordFocusCommentId);
  const record = useProjectStore((s) => (s.recordFocusId ? s.project.records.find((r) => r.id === s.recordFocusId) : undefined));

  const close = () => {
    const s = useProjectStore.getState();
    if (s.recordFocusIsNew && s.recordFocusId) s.deleteRecords([s.recordFocusId]);
    s.setRecordFocus(null);
  };

  useEffect(() => {
    if (!id) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      e.preventDefault();
      close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [id]);

  if (!record || !id) return null;
  return (
    <OverlayDrawer
      open
      position="end"
      modalType="alert"
      className={styles.drawer}
      backdrop={{ className: styles.backdrop, onMouseDown: (e) => e.preventDefault() }}
      onOpenChange={(e, d) => {
        e.preventDefault();
        if (d.type === "backdropClick") return;
        if (!d.open) close();
      }}
      aria-label={`${RECORD_KIND_LABEL[record.kind]} 상세`}
    >
      <DrawerContent key={`${id}:${focusTab}:${focusCommentId ?? ""}`} record={record} onClose={close} initialTab={focusTab} focusCommentId={focusCommentId} />
    </OverlayDrawer>
  );
}

const SOURCE_OPTIONS = [{ value: "", label: "미정" }, ...SOURCE_ORDER.map((s) => ({ value: s, label: SOURCE_LABEL[s] }))];

function DrawerContent({ record, onClose, initialTab, focusCommentId }: { record: ProjectRecord; onClose: () => void; initialTab: DetailTab; focusCommentId: string | null }) {
  const styles = useStyles();
  const surface = usePlanSurface();
  const own = useOwnWork();
  const canDraft = useCan("plan.record.draft");
  const canComment = useCan("plan.comment.write");
  const documentNotice = useTombstoneSentence("record", [record.id]);
  const ctx = useTaskContext();
  const project = useProjectStore((s) => s.project);
  const projectId = useProjectStore((s) => s.activeId) ?? "";
  const isNew = useProjectStore((s) => s.recordFocusIsNew);
  const baseWrite = record.kind === "policy" || record.kind === "decision" || record.kind === "assumption" ? canDraft : surface.editIssue;
  const registering = !own.restricted || isNew;
  const canWrite = baseWrite && registering;
  const s = useProjectStore.getState;
  const globalResources = useCatalogStore((st) => st.resources);
  const [draft, setDraft] = useState<ProjectRecord>(() => structuredClone(record));
  const [tab, setTab] = useState<DetailTab>(initialTab);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const titleRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isNew) return;
    const t = setTimeout(() => titleRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [isNew]);

  const suggestions = useMemo(() => {
    const fromAssignments = (project.resourceAssignments || []).map((a) => globalResources[a.resourceId]?.displayName).filter((n): n is string => Boolean(n));
    return Array.from(new Set([...fromAssignments, ...knownAssignees(project)]));
  }, [project, globalResources]);

  const taskOptions = useMemo<RefOption[]>(
    () =>
      project.executionTasks.map((t) => ({
        id: t.id,
        label: t.title || "제목 없는 Task",
        sub: t.status ? STATUS_LABEL[t.status] : undefined,
        media: <TaskListSquareLtr16Regular />,
      })),
    [project.executionTasks],
  );
  const recordOptions = useMemo<RefOption[]>(
    () =>
      project.records
        .filter((r) => r.id !== record.id)
        .map((r) => ({ id: r.id, code: recordCode(r), label: r.title || "제목 없음", sub: recordStatusLabel(r.kind, r.status), media: recordKindIcon(r.kind) })),
    [project.records, record.id],
  );
  const referencing = useMemo(() => recordsReferencing(project.records, record.id), [project.records, record.id]);
  const successor = useMemo(() => supersededBy(project.records, record.id), [project.records, record.id]);
  const supersedeOptions = useMemo(() => {
    const later = new Set<string>();
    let cur: string | undefined = record.id;
    // exclude records that (transitively) replace this one so the chain can't loop
    while (cur) {
      const next = supersededBy(project.records, cur);
      if (!next || later.has(next.id)) break;
      later.add(next.id);
      cur = next.id;
    }
    return [
      { value: "", label: "없음" },
      ...project.records
        .filter((r) => r.kind === record.kind && r.id !== record.id && !later.has(r.id))
        .map((r) => ({ value: r.id, label: `${recordCode(r)} ${r.title || "제목 없음"}`, description: recordStatusLabel(r.kind, r.status) })),
    ];
  }, [project.records, record.id, record.kind]);

  const commentHandlers = useMemo<CommentThreadHandlers>(
    () => ({
      onAdd: (body, options) => s().addRecordComment(record.id, body, options),
      onUpdate: (commentId, body, options) => s().updateRecordComment(record.id, commentId, body, options),
      onDelete: (commentId) => s().deleteRecordComment(record.id, commentId),
    }),
    [s, record.id],
  );

  const update = (patch: Partial<ProjectRecord>) => setDraft((prev) => ({ ...prev, ...patch }));
  const setSection = (key: keyof ProjectRecord["sections"], value: string) =>
    setDraft((prev) => {
      const sections = { ...prev.sections };
      if (value) sections[key] = value;
      else delete sections[key];
      return { ...prev, sections };
    });

  /** Push the draft to the store. */
  const commitDraft = () => {
    const title = draft.title.trim() || (isNew ? `새 ${RECORD_KIND_LABEL[record.kind]}` : record.title);
    const patch: Partial<ProjectRecord> = {};
    if (title !== record.title) patch.title = title;
    if (draft.status !== record.status) patch.status = draft.status;
    if (draft.severity !== record.severity) patch.severity = draft.severity;
    if (draft.probability !== record.probability) patch.probability = draft.probability;
    if (draft.impactScore !== record.impactScore) patch.impactScore = draft.impactScore;
    if (draft.source !== record.source) patch.source = draft.source;
    if (draft.assignees.join("|") !== record.assignees.join("|")) patch.assignees = draft.assignees;
    if (draft.due !== record.due) patch.due = draft.due;
    if (draft.supersedes !== record.supersedes) patch.supersedes = draft.supersedes;
    if (draft.impacts.join("|") !== record.impacts.join("|")) patch.impacts = draft.impacts;
    if (JSON.stringify(draft.sections) !== JSON.stringify(record.sections)) patch.sections = draft.sections;
    if (draft.linkedTaskIds.join("|") !== record.linkedTaskIds.join("|")) patch.linkedTaskIds = draft.linkedTaskIds;
    if (draft.linkedExecutionTaskIds.join("|") !== record.linkedExecutionTaskIds.join("|")) patch.linkedExecutionTaskIds = draft.linkedExecutionTaskIds;
    if (draft.linkedRecordIds.join("|") !== record.linkedRecordIds.join("|")) patch.linkedRecordIds = draft.linkedRecordIds;
    if (JSON.stringify(draft.attachments) !== JSON.stringify(record.attachments)) patch.attachments = draft.attachments;
    if (Object.keys(patch).length) s().updateRecord(record.id, patch);
    if (isNew) s().setRecordFocus(record.id);
  };

  /** Attachments are edited outside the 상세 form, so they save as soon as they change. */
  const changeAttachments = (attachments: ProjectRecord["attachments"]) => {
    update({ attachments });
    if (!isNew) s().updateRecord(record.id, { attachments });
  };

  const handleSave = () => {
    commitDraft();
    s().setRecordFocus(null);
  };

  const handleCancel = () => {
    if (isNew) s().deleteRecords([record.id]);
    s().setRecordFocus(null);
  };

  const openOtherRecord = (id: string) => {
    if (canWrite) commitDraft();
    s().setRecordFocus(id);
  };

  /** Hand off to the Task sheet; closing it comes back to this record. */
  const openTask = (taskId: string) => {
    if (canWrite) commitDraft();
    s().setExecutionFocus(taskId, { returnToRecordId: record.id });
  };

  const createTask = () => {
    commitDraft();
    s().createTaskForRecord(record.id);
  };

  /** Title-only Task created in place, without leaving the record sheet. */
  const quickAddTask = (title: string) => {
    commitDraft();
    const taskId = s().createTaskForRecord(record.id, { open: false, title });
    const fresh = s().project.records.find((r) => r.id === record.id);
    if (taskId && fresh) setDraft((prev) => ({ ...prev, linkedExecutionTaskIds: fresh.linkedExecutionTaskIds }));
  };

  const linkTask = (taskId: string) => {
    if (draft.linkedExecutionTaskIds.includes(taskId)) return;
    update({ linkedExecutionTaskIds: [...draft.linkedExecutionTaskIds, taskId] });
  };

  const unlinkTask = (taskId: string) => update({ linkedExecutionTaskIds: draft.linkedExecutionTaskIds.filter((x) => x !== taskId) });

  const revise = () => {
    commitDraft();
    s().reviseRecord(record.id);
  };

  const raiseIssue = () => {
    commitDraft();
    s().raiseIssueFromRisk(record.id);
  };

  const refs = draft.linkedTaskIds.map((tid) => scopeRef(tid, ctx));
  const linkedTasks = draft.linkedExecutionTaskIds.map((id) => project.executionTasks.find((t) => t.id === id)).filter((t): t is NonNullable<typeof t> => Boolean(t));
  const decisionLike = record.kind === "decision" || record.kind === "policy";
  const supersededTarget = draft.supersedes ? project.records.find((r) => r.id === draft.supersedes) : undefined;
  const statusOptions = recordStatusOptions(record.kind);
  const severitySelectOptions = severityOptions();
  const activeComments = record.comments.filter((c) => !c.deleted).length;
  const showDetails = isNew || tab === "details";

  return (
    <>
      <DrawerHeader className={styles.header}>
        <div className={styles.headerTop}>
          <Caption1 className={styles.headerMeta}>
            <span aria-hidden>{recordKindIcon(record.kind)}</span>
            {isNew ? `새 ${RECORD_KIND_LABEL[record.kind]}` : RECORD_KIND_LABEL[record.kind]}
            <span className={styles.code}>{recordCode(record)}</span>
          </Caption1>
          <div className={styles.headerActions}>
            {!isNew && canWrite && (
              <Menu>
                <MenuTrigger disableButtonEnhancement>
                  <FuiButton appearance="subtle" icon={<MoreHorizontal20Regular />} aria-label="더 보기" />
                </MenuTrigger>
                <MenuPopover>
                  <MenuList>
                    <MenuItem onClick={createTask}>대응 Task 만들기</MenuItem>
                    {decisionLike && <MenuItem onClick={revise}>개정안 만들기</MenuItem>}
                    {record.kind === "risk" && <MenuItem onClick={raiseIssue}>발생 — 이슈로 등록</MenuItem>}
                    <MenuDivider />
                    <MenuItem icon={<Delete16Regular />} onClick={() => setConfirmDelete(true)}>
                      삭제
                    </MenuItem>
                  </MenuList>
                </MenuPopover>
              </Menu>
            )}
            <FuiButton appearance="subtle" icon={<Dismiss24Regular />} aria-label="닫기" onClick={isNew ? handleCancel : onClose} />
          </div>
        </div>
        {canWrite ? (
          <Input
            ref={titleRef}
            appearance="underline"
            className={styles.titleInput}
            value={draft.title}
            placeholder={`${RECORD_KIND_LABEL[record.kind]} 제목`}
            aria-label="제목"
            onChange={(e) => update({ title: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.nativeEvent.isComposing) (e.target as HTMLInputElement).blur();
            }}
          />
        ) : (
          <div className={styles.titleRead}>{draft.title || "제목 없음"}</div>
        )}
        {!isNew && (
          <DetailTabs
            value={tab}
            onChange={setTab}
            counts={{ comments: activeComments, attachments: record.attachments.length, activity: record.activity.length }}
            ariaLabel={`${RECORD_KIND_LABEL[record.kind]} 상세 탭`}
          />
        )}
      </DrawerHeader>

      <DrawerBody className={mergeClasses(styles.body, !showDetails && styles.bodyFeed)}>
        {showDetails ? (
          <>
            {canWrite ? (
            <>
            {hasSeverity(record.kind) ? (
              <div className={styles.grid2}>
                <FuiFilterSelect label="상태" value={draft.status} options={statusOptions} onValueChange={(v) => update({ status: v as RecordStatus })} style={{ width: "100%" }} />
                <FuiFilterSelect label="심각도" value={draft.severity ?? ""} options={severitySelectOptions} onValueChange={(v) => update({ severity: (v || undefined) as RecordSeverity | undefined })} style={{ width: "100%" }} />
              </div>
            ) : (
              <FuiFilterSelect label="상태" value={draft.status} options={statusOptions} onValueChange={(v) => update({ status: v as RecordStatus })} style={{ width: "100%" }} />
            )}

            {record.kind === "risk" ? (
              <div className={styles.grid2}>
                <FuiFilterSelect
                  label="가능성"
                  value={draft.probability ? String(draft.probability) : ""}
                  options={[{ value: "", label: "미정" }, ...RISK_SCALES.map((scale) => ({ value: String(scale), label: `${scale} ${RISK_SCALE_LABEL[scale]}` }))]}
                  onValueChange={(value) => update({ probability: value ? (Number(value) as RiskScale) : undefined })}
                  style={{ width: "100%" }}
                />
                <FuiFilterSelect
                  label="영향 정도"
                  value={draft.impactScore ? String(draft.impactScore) : ""}
                  options={[{ value: "", label: "미정" }, ...RISK_SCALES.map((scale) => ({ value: String(scale), label: `${scale} ${RISK_SCALE_LABEL[scale]}` }))]}
                  onValueChange={(value) => update({ impactScore: value ? (Number(value) as RiskScale) : undefined })}
                  style={{ width: "100%" }}
                />
                <Caption1 className={styles.muted}>
                  {riskScore(draft) == null ? "점수는 가능성과 영향 정도를 고르면 나옵니다." : `점수 ${riskScore(draft)}`}
                </Caption1>
              </div>
            ) : null}

            {(record.kind === "change" && (draft.status === "approved" || draft.status === "applied") && surface.createBaseline) ? (
              <FuiButton appearance="secondary" onClick={() => useProjectStore.getState().saveBaseline(`변경 ${recordCode(record)}`)}>
                이 변경을 기준선으로 남기기
              </FuiButton>
            ) : null}

            <Field label="담당자" size="small">
              <AssigneePicker value={draft.assignees} onChange={(assignees) => update({ assignees })} suggestions={suggestions} />
            </Field>

            <div className={styles.grid2}>
              <Field label="출처" size="small">
                <FuiDropdown
                  value={draft.source ?? ""}
                  options={SOURCE_OPTIONS}
                  onValueChange={(v) => update({ source: (v || undefined) as RecordSource | undefined })}
                  aria-label="출처"
                />
              </Field>
              <Field label={dueLabel(record.kind)} size="small">
                <DateInput value={draft.due} onChange={(due) => update({ due })} ariaLabel={dueLabel(record.kind)} />
              </Field>
            </div>

            {record.kind !== "policy" && (
              <DetailSection title="영향">
                <div className={styles.impacts} role="group" aria-label="영향 범위">
                  {IMPACT_ORDER.map((imp) => (
                    <Checkbox
                      key={imp}
                      className={styles.impactCheck}
                      label={IMPACT_LABEL[imp]}
                      checked={draft.impacts.includes(imp)}
                      onChange={(_, d) => update({ impacts: IMPACT_ORDER.filter((x) => (x === imp ? !!d.checked : draft.impacts.includes(x))) as RecordImpact[] })}
                    />
                  ))}
                </div>
              </DetailSection>
            )}

            {RECORD_SECTIONS[record.kind].map((def) => (
              <Field key={def.key} label={def.label} size="small">
                <FuiTextarea rows={3} value={draft.sections[def.key] ?? ""} placeholder={def.placeholder} onChange={(_, d) => setSection(def.key, d.value)} />
              </Field>
            ))}

            {decisionLike && (
              <DetailSection title="대체 관계">
                {successor && (
                  <div className={styles.notice}>
                    <Caption1>이 {RECORD_KIND_LABEL[record.kind]}은(는) 다음으로 대체되었습니다</Caption1>
                    <RecordChip record={successor} onOpen={openOtherRecord} />
                  </div>
                )}
                <FuiDropdown
                  value={draft.supersedes ?? ""}
                  options={supersedeOptions}
                  onValueChange={(v) => update({ supersedes: v || undefined })}
                  placeholder="대체하는 이전 기록 없음"
                  prefix="이전 기록"
                  aria-label="이 기록이 대체하는 이전 기록"
                />
                {supersededTarget && (
                  <Caption1 className={styles.muted}>
                    이 기록을 확정하면 {recordCode(supersededTarget)}은(는) 「대체됨」으로 바뀝니다. 이전 기록의 내용은 그대로 남습니다.
                  </Caption1>
                )}
              </DetailSection>
            )}

            <DetailSection icon={<Link16Regular />} title="연결된 WBS · 작업" hint={refs.length ? `${refs.length}개` : undefined}>
              <ScopePicker value={draft.linkedTaskIds} onChange={(linkedTaskIds) => update({ linkedTaskIds })} ctx={ctx} />
              {refs.length > 0 && (
                <div className={styles.chips}>
                  {refs.map((r) => (
                    <ScopeChip
                      key={r.id}
                      ref={r}
                      onOpen={(tid) => {
                        commitDraft();
                        s().setRecordFocus(null);
                        openScopeInSchedule(projectId, tid);
                      }}
                    />
                  ))}
                </div>
              )}
            </DetailSection>

            <RecordTaskSection
              tasks={linkedTasks}
              options={taskOptions}
              canCreate={!isNew}
              onOpen={openTask}
              onCreateDetailed={createTask}
              onQuickAdd={quickAddTask}
              onLink={linkTask}
              onUnlink={unlinkTask}
            />

            {!isNew && (
              <DetailSection icon={<Document16Regular />} title="문서">
                <LinkedDocumentRows kind="record" targetId={record.id} canEdit={canWrite} />
              </DetailSection>
            )}

            <DetailSection icon={<ClipboardTextLtr16Regular />} title="관련 기록" hint={draft.linkedRecordIds.length ? `${draft.linkedRecordIds.length}개` : undefined}>
              <RefPicker value={draft.linkedRecordIds} onChange={(linkedRecordIds) => update({ linkedRecordIds })} options={recordOptions} placeholder="번호나 제목으로 찾아 연결" ariaLabel="관련 기록 연결" emptyText="일치하는 기록이 없습니다" />
              {referencing.length > 0 && (
                <>
                  <Caption1 className={styles.muted}>이 기록을 참조하는 기록</Caption1>
                  <div className={styles.chips}>
                    {referencing.map((r) => (
                      <RecordChip key={r.id} record={r} onOpen={openOtherRecord} />
                    ))}
                  </div>
                </>
              )}
            </DetailSection>

            {isNew && (
              <DetailSection icon={<Attach16Regular />} title="첨부 파일 및 링크" hint={draft.attachments.length ? `${draft.attachments.length}개` : undefined} collapsible defaultOpen={false}>
                <AttachmentPanel value={draft.attachments} onChange={changeAttachments} />
              </DetailSection>
            )}
            </>
            ) : (
            <>
            <Field label="상태" size="small">
              <RecordStatusBadge kind={record.kind} status={draft.status} />
            </Field>
            {hasSeverity(record.kind) && (
              <Field label="심각도" size="small">
                <SeverityBadge severity={draft.severity} />
              </Field>
            )}
            <Field label="담당자" size="small">
              <Caption1>{draft.assignees.join(", ") || "—"}</Caption1>
            </Field>
            <div className={styles.grid2}>
              <Field label="출처" size="small">
                <Caption1>{draft.source ? SOURCE_LABEL[draft.source] : "—"}</Caption1>
              </Field>
              <Field label={dueLabel(record.kind)} size="small">
                <Caption1>{draft.due ? formatDate(draft.due, "full") : "—"}</Caption1>
              </Field>
            </div>
            {record.kind !== "policy" && (
              <DetailSection title="영향">
                <Caption1>{draft.impacts.length ? draft.impacts.map((imp) => IMPACT_LABEL[imp]).join(", ") : "—"}</Caption1>
              </DetailSection>
            )}
            {RECORD_SECTIONS[record.kind].map((def) => (
              <Field key={def.key} label={def.label} size="small">
                <Caption1 style={{ whiteSpace: "pre-wrap" }}>{draft.sections[def.key] || "—"}</Caption1>
              </Field>
            ))}
            {decisionLike && (successor || supersededTarget) && (
              <DetailSection title="대체 관계">
                {successor && (
                  <div className={styles.notice}>
                    <Caption1>이 {RECORD_KIND_LABEL[record.kind]}은(는) 다음으로 대체되었습니다</Caption1>
                    <RecordChip record={successor} onOpen={openOtherRecord} />
                  </div>
                )}
                {supersededTarget && (
                  <div className={styles.notice}>
                    <Caption1>이전 기록</Caption1>
                    <RecordChip record={supersededTarget} onOpen={openOtherRecord} />
                  </div>
                )}
              </DetailSection>
            )}
            {refs.length > 0 && (
              <DetailSection icon={<Link16Regular />} title="연결된 WBS · 작업" hint={`${refs.length}개`}>
                <div className={styles.chips}>
                  {refs.map((r) => (
                    <ScopeChip key={r.id} ref={r} onOpen={(tid) => openScopeInSchedule(projectId, tid)} />
                  ))}
                </div>
              </DetailSection>
            )}
            <RecordTaskSection
              tasks={linkedTasks}
              options={taskOptions}
              canCreate={false}
              readOnly
              onOpen={openTask}
              onCreateDetailed={createTask}
              onQuickAdd={quickAddTask}
              onLink={linkTask}
              onUnlink={unlinkTask}
            />
            <DetailSection icon={<Document16Regular />} title="문서">
              <LinkedDocumentRows kind="record" targetId={record.id} />
            </DetailSection>
            {(draft.linkedRecordIds.length > 0 || referencing.length > 0) && (
              <DetailSection icon={<ClipboardTextLtr16Regular />} title="관련 기록" hint={draft.linkedRecordIds.length ? `${draft.linkedRecordIds.length}개` : undefined}>
                {draft.linkedRecordIds.length > 0 && (
                  <div className={styles.chips}>
                    {draft.linkedRecordIds.map((id) => {
                      const linked = project.records.find((r) => r.id === id);
                      return linked ? <RecordChip key={id} record={linked} onOpen={openOtherRecord} /> : null;
                    })}
                  </div>
                )}
                {referencing.length > 0 && (
                  <>
                    <Caption1 className={styles.muted}>이 기록을 참조하는 기록</Caption1>
                    <div className={styles.chips}>
                      {referencing.map((r) => (
                        <RecordChip key={r.id} record={r} onOpen={openOtherRecord} />
                      ))}
                    </div>
                  </>
                )}
              </DetailSection>
            )}
            </>
            )}

            {!isNew && (
              <Caption1 className={styles.muted}>
                등록 {formatDate(record.createdAt, "full")} · 수정 {formatDate(record.updatedAt, "dateTime")}
                {record.closedAt ? ` · 종결 ${formatDate(record.closedAt, "dateTime")}` : ""}
              </Caption1>
            )}
          </>
        ) : tab === "comments" ? (
          <CommentThread comments={record.comments} focusCommentId={focusCommentId} readOnly={!canComment} {...commentHandlers} />
        ) : tab === "attachments" ? (
          <AttachmentPanel value={draft.attachments} onChange={changeAttachments} readOnly={!canWrite} />
        ) : (
          <TaskActivityLog items={record.activity} />
        )}
      </DrawerBody>

      {canWrite && showDetails && (
        <DrawerFooter className={styles.footer}>
          <FuiButton appearance="secondary" onClick={isNew ? handleCancel : onClose}>
            취소
          </FuiButton>
          <FuiButton appearance="primary" onClick={handleSave}>
            저장
          </FuiButton>
        </DrawerFooter>
      )}

      <ConfirmDialog
        open={confirmDelete}
        title={`${RECORD_KIND_LABEL[record.kind]} 삭제`}
        message={
          <>
            이 기록을 삭제할까요? 댓글과 첨부 파일도 함께 사라지고, 다른 기록·Task에서 이 기록으로 걸린 연결은 끊어집니다. {documentNotice ? `${documentNotice} ` : ""}효력을
            없애려는 것이라면 삭제 대신 상태를 {decisionLike ? "「폐기」·「기각」" : "「종결」"}으로 바꾸세요.
          </>
        }
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => {
          setConfirmDelete(false);
          s().deleteRecords([record.id]);
        }}
      />
    </>
  );
}
